<template lang="">
    <div>
        <div class="promotion-action-bar">
            <AppBtn class="cancel-btn" :noMargin="true" condensed @click="cancel"> Cancel </AppBtn>
            <AppBtn class="save-btn" :noMargin="true" condensed @click="updateData">
                {{ processing ? "Please wait..." : editMode ? "Update" : "Save" }}
            </AppBtn>
        </div>
        
        <!-- Customer Group Promotion Banner -->
        <div v-if="isCustomerGroupPromotion" class="alert customer-group-banner mb-4">
            <div class="d-flex align-items-center">
                <i class="fas fa-users-cog mr-3 banner-icon"></i>
                <div>
                    <h5 class="mb-1">Customer Group Promotion</h5>
                    <p class="mb-0">This promotion will apply discounts based on customer groups only. Product and location filters are disabled.</p>
                </div>
            </div>
        </div>
        
        <Card class="promotion-card compact-card" :spaceBetween="false" title="Basic Details">
            <ProductSearchModal
                :defaultSelectedProducts="selectedProducts"
                @selectedProducts="addSelectedProducts"
                :show="showProductModal"
                @show="showProductModal = false"
            />
            <CustomerSearchModal
                :defaultSelected="selectedCustomerGroups"
                @customers="addSelectedCustomerGroups"
                :show="showCustomerModal"
                @show="showCustomerModal = false"
            />
            <div>
                <div v-if="loading" class="loading-indicator">
                    <div class="spinner"></div>
                    Loading...
                </div>
                <div class="compact-form" v-if="!loading">
                    <div class="form-grid">
                        <CustomInput class="name-field" label="Promotion name" v-model="name" />
                        <div class="value-field">
                            <label class="input-label" for="promotion-discount-value">{{ discountValueLabel }}</label>
                            <div class="discount-value-input">
                                <span v-if="discountValuePrefix" class="discount-value-affix">{{ discountValuePrefix }}</span>
                                <input
                                    id="promotion-discount-value"
                                    v-model="rewardValue"
                                    type="number"
                                    :min="discountValueMinimum"
                                    :max="discountValueMaximum"
                                    step="0.01"
                                    inputmode="decimal"
                                />
                                <span v-if="discountValueSuffix" class="discount-value-affix">{{ discountValueSuffix }}</span>
                            </div>
                            <small class="discount-value-help">{{ discountValueHelp }}</small>
                        </div>
                        <div class="status-toggle">
                            <label class="toggle-label">{{ status ? "Active" : "Inactive" }}</label>
                            <div 
                                class="toggle-switch-custom" 
                                :class="{ 'active': status }" 
                                @click="toggleStatus"
                            >
                                <div class="toggle-knob"></div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Customer Group Promotion Toggle -->
                    <div class="customer-group-toggle-row">
                        <div class="custom-checkbox">
                            <input type="checkbox" id="customer-group-promotion" v-model="isCustomerGroupPromotion" @change="handleCustomerGroupPromotionChange" />
                            <label for="customer-group-promotion">
                                <strong>Make this a Customer Group Promotion</strong>
                                <small class="text-muted d-block">When enabled, this promotion will apply to all products at all locations for customers in the selected group(s)</small>
                            </label>
                        </div>
                    </div>

                    <div class="discount-rules-grid">
                        <div class="rule-control rule-control-promotion">
                            <label class="input-label">Promotion type</label>
                            <select class="form-control compact-select" v-model="promotionType" @change="handlePromotionTypeChange">
                                <option value="product_discount">Product</option>
                                <option value="bogo_discounted">BOGO</option>
                                <option value="bundle_discount">Bundle</option>
                            </select>
                        </div>
                        <div class="rule-control">
                            <label class="input-label">Discount logic</label>
                            <select class="form-control compact-select" v-model="discountMode" disabled>
                                <option value="best_price">Best price (no stacking)</option>
                            </select>
                        </div>
                        <div class="rule-control">
                            <label class="input-label">Discount as</label>
                            <div class="segmented-control compact-segmented-control">
                                <button
                                    v-for="option in availableRewardApplicationOptions"
                                    :key="option.value"
                                    type="button"
                                    :class="['segmented-option', { active: rewardApplication === option.value }]"
                                    @click="setRewardApplication(option.value)"
                                >
                                    {{ option.label }}
                                </button>
                            </div>
                        </div>
                        <div class="custom-checkbox display-toggle compact-display-toggle">
                            <input type="checkbox" id="show-on-sales-dashboard" v-model="showOnSalesDashboard" />
                            <label for="show-on-sales-dashboard">
                                <strong>Show on POS Sales Screen</strong>
                                <small class="text-muted d-block">Advertise this offer to employees while building a cart.</small>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </Card>

        <Card
            v-if="!loading && promotionType === 'bogo_discounted' && !isCustomerGroupPromotion"
            :spaceBetween="false"
            class="my-4 promotion-card rule-builder-card"
            title="BOGO Rule"
        >
            <div class="rule-builder-grid">
                <div class="rule-panel">
                    <div class="rule-panel-header">
                        <span>Buy</span>
                    </div>
                    <CustomInput label="Quantity" type="number" v-model="bogoRule.buy.quantity" />
                    <div class="rule-scope-block">
                        <label class="input-label">Buy scope</label>
                        <select
                            class="form-control compact-select"
                            v-model="bogoRule.buy.scope.type"
                            @change="handleRuleScopeTypeChange(bogoRule.buy.scope)"
                        >
                            <option v-for="option in ruleScopeModes" :key="'buy-' + option.value" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                        <div v-if="bogoRule.buy.scope.type === 'all'" class="text-muted mt-2">
                            Applies to every product.
                        </div>
                        <MultiSelectChip
                            v-if="bogoRule.buy.scope.type === 'categories'"
                            title="Select buy categories"
                            :vertical-chips="true"
                            :vertical-chips-row="true"
                            :extraText="searchingCategories ? 'Searching...' : ''"
                            :options="categoryOptions"
                            :selectedValues="bogoRule.buy.scope.values"
                            @search="searchCategories"
                            @update="handleRuleScopeValues(bogoRule.buy.scope, $event)"
                        />
                        <MultiSelectChip
                            v-if="bogoRule.buy.scope.type === 'brands'"
                            title="Select buy brands"
                            :vertical-chips="true"
                            :vertical-chips-row="true"
                            :extraText="searchingBrands ? 'Searching...' : ''"
                            :options="brandOptions"
                            :selectedValues="bogoRule.buy.scope.values"
                            @search="searchBrands"
                            @update="handleRuleScopeValues(bogoRule.buy.scope, $event)"
                        />
                        <MultiSelectChip
                            v-if="bogoRule.buy.scope.type === 'suppliers'"
                            title="Select buy suppliers"
                            :vertical-chips="true"
                            :vertical-chips-row="true"
                            :extraText="searchingSuppliers ? 'Searching...' : ''"
                            :options="supplierOptions"
                            :selectedValues="bogoRule.buy.scope.values"
                            @search="searchSuppliers"
                            @update="handleRuleScopeValues(bogoRule.buy.scope, $event)"
                        />
                        <MultiSelectChip
                            v-if="bogoRule.buy.scope.type === 'master_products'"
                            title="Select buy master products"
                            :vertical-chips="true"
                            :vertical-chips-row="true"
                            :extraText="masterProductSearchMessage"
                            :options="masterProductOptions"
                            :selectedValues="bogoRule.buy.scope.values"
                            @search="searchMasterProducts"
                            @update="handleRuleScopeValues(bogoRule.buy.scope, $event)"
                        />
                        <MultiSelectChip
                            v-if="bogoRule.buy.scope.type === 'product_skus'"
                            title="Select buy SKUs"
                            :vertical-chips="true"
                            :vertical-chips-row="true"
                            :extraText="searchingProducts ? 'Searching...' : ''"
                            :options="productSkuOptions"
                            :selectedValues="bogoRule.buy.scope.values"
                            @search="searchProductsWithSKUs"
                            @update="handleRuleScopeValues(bogoRule.buy.scope, $event)"
                        />
                    </div>
                </div>

                <div class="rule-panel">
                    <div class="rule-panel-header">
                        <span>Get</span>
                    </div>
                    <CustomInput label="Quantity" type="number" v-model="bogoRule.get.quantity" />
                    <div class="rule-scope-block">
                        <label class="input-label">Get scope</label>
                        <select
                            class="form-control compact-select"
                            v-model="bogoRule.get.scope.type"
                            @change="handleRuleScopeTypeChange(bogoRule.get.scope)"
                        >
                            <option v-for="option in ruleScopeModes" :key="'get-' + option.value" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                        <div v-if="bogoRule.get.scope.type === 'all'" class="text-muted mt-2">
                            Applies to every product.
                        </div>
                        <MultiSelectChip
                            v-if="bogoRule.get.scope.type === 'categories'"
                            title="Select get categories"
                            :vertical-chips="true"
                            :vertical-chips-row="true"
                            :extraText="searchingCategories ? 'Searching...' : ''"
                            :options="categoryOptions"
                            :selectedValues="bogoRule.get.scope.values"
                            @search="searchCategories"
                            @update="handleRuleScopeValues(bogoRule.get.scope, $event)"
                        />
                        <MultiSelectChip
                            v-if="bogoRule.get.scope.type === 'brands'"
                            title="Select get brands"
                            :vertical-chips="true"
                            :vertical-chips-row="true"
                            :extraText="searchingBrands ? 'Searching...' : ''"
                            :options="brandOptions"
                            :selectedValues="bogoRule.get.scope.values"
                            @search="searchBrands"
                            @update="handleRuleScopeValues(bogoRule.get.scope, $event)"
                        />
                        <MultiSelectChip
                            v-if="bogoRule.get.scope.type === 'suppliers'"
                            title="Select get suppliers"
                            :vertical-chips="true"
                            :vertical-chips-row="true"
                            :extraText="searchingSuppliers ? 'Searching...' : ''"
                            :options="supplierOptions"
                            :selectedValues="bogoRule.get.scope.values"
                            @search="searchSuppliers"
                            @update="handleRuleScopeValues(bogoRule.get.scope, $event)"
                        />
                        <MultiSelectChip
                            v-if="bogoRule.get.scope.type === 'master_products'"
                            title="Select get master products"
                            :vertical-chips="true"
                            :vertical-chips-row="true"
                            :extraText="masterProductSearchMessage"
                            :options="masterProductOptions"
                            :selectedValues="bogoRule.get.scope.values"
                            @search="searchMasterProducts"
                            @update="handleRuleScopeValues(bogoRule.get.scope, $event)"
                        />
                        <MultiSelectChip
                            v-if="bogoRule.get.scope.type === 'product_skus'"
                            title="Select get SKUs"
                            :vertical-chips="true"
                            :vertical-chips-row="true"
                            :extraText="searchingProducts ? 'Searching...' : ''"
                            :options="productSkuOptions"
                            :selectedValues="bogoRule.get.scope.values"
                            @search="searchProductsWithSKUs"
                            @update="handleRuleScopeValues(bogoRule.get.scope, $event)"
                        />
                    </div>
                </div>
            </div>
            <div class="custom-checkbox mt-3">
                <input type="checkbox" id="bogo-repeat" v-model="bogoRule.repeat" />
                <label for="bogo-repeat">Repeat this rule for every complete set in the cart</label>
            </div>
        </Card>

        <Card
            v-if="!loading && promotionType === 'bundle_discount' && !isCustomerGroupPromotion"
            :spaceBetween="false"
            class="my-4 promotion-card rule-builder-card"
            title="Bundle Rule"
        >
            <div class="bundle-group-list">
                <div class="bundle-group-panel" v-for="(group, index) in bundleRule.groups" :key="group.id">
                    <div class="rule-panel-header">
                        <span>{{ group.name || ('Group ' + (index + 1)) }}</span>
                        <button
                            v-if="bundleRule.groups.length > 2"
                            type="button"
                            class="btn btn-sm btn-outline-danger"
                            @click="removeBundleGroup(index)"
                        >
                            Remove
                        </button>
                    </div>
                    <div class="bundle-group-fields">
                        <CustomInput label="Group name" v-model="group.name" />
                        <CustomInput label="Quantity" type="number" v-model="group.quantity" />
                    </div>
                    <div class="rule-scope-block">
                        <label class="input-label">Group scope</label>
                        <select class="form-control compact-select" v-model="group.scope.type" @change="handleRuleScopeTypeChange(group.scope)">
                            <option v-for="option in ruleScopeModes" :key="group.id + '-' + option.value" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                        <div v-if="group.scope.type === 'all'" class="text-muted mt-2">
                            Applies to every product.
                        </div>
                        <MultiSelectChip
                            v-if="group.scope.type === 'categories'"
                            title="Select group categories"
                            :vertical-chips="true"
                            :vertical-chips-row="true"
                            :extraText="searchingCategories ? 'Searching...' : ''"
                            :options="categoryOptions"
                            :selectedValues="group.scope.values"
                            @search="searchCategories"
                            @update="handleRuleScopeValues(group.scope, $event)"
                        />
                        <MultiSelectChip
                            v-if="group.scope.type === 'brands'"
                            title="Select group brands"
                            :vertical-chips="true"
                            :vertical-chips-row="true"
                            :extraText="searchingBrands ? 'Searching...' : ''"
                            :options="brandOptions"
                            :selectedValues="group.scope.values"
                            @search="searchBrands"
                            @update="handleRuleScopeValues(group.scope, $event)"
                        />
                        <MultiSelectChip
                            v-if="group.scope.type === 'suppliers'"
                            title="Select group suppliers"
                            :vertical-chips="true"
                            :vertical-chips-row="true"
                            :extraText="searchingSuppliers ? 'Searching...' : ''"
                            :options="supplierOptions"
                            :selectedValues="group.scope.values"
                            @search="searchSuppliers"
                            @update="handleRuleScopeValues(group.scope, $event)"
                        />
                        <MultiSelectChip
                            v-if="group.scope.type === 'master_products'"
                            title="Select group master products"
                            :vertical-chips="true"
                            :vertical-chips-row="true"
                            :extraText="masterProductSearchMessage"
                            :options="masterProductOptions"
                            :selectedValues="group.scope.values"
                            @search="searchMasterProducts"
                            @update="handleRuleScopeValues(group.scope, $event)"
                        />
                        <MultiSelectChip
                            v-if="group.scope.type === 'product_skus'"
                            title="Select group SKUs"
                            :vertical-chips="true"
                            :vertical-chips-row="true"
                            :extraText="searchingProducts ? 'Searching...' : ''"
                            :options="productSkuOptions"
                            :selectedValues="group.scope.values"
                            @search="searchProductsWithSKUs"
                            @update="handleRuleScopeValues(group.scope, $event)"
                        />
                    </div>
                </div>
            </div>
            <div class="bundle-actions">
                <button type="button" class="btn btn-outline-primary" @click="addBundleGroup">Add group</button>
                <div class="custom-checkbox">
                    <input type="checkbox" id="bundle-repeat" v-model="bundleRule.repeat" />
                    <label for="bundle-repeat">Repeat this bundle for every complete set in the cart</label>
                </div>
            </div>
        </Card>

        <div class="row gy-3" v-if="!loading">
            <div class="col-md-6" v-if="!isCustomerGroupPromotion">
                <Card :spaceBetween="false" class="my-4 promotion-card" title="Locations">
                    <div class="mb-3">
                        <div class="custom-checkbox">
                            <input type="checkbox" id="all-locations" v-model="allLocations" @change="handleAllLocationsChange" />
                            <label for="all-locations">Apply to all locations</label>
                        </div>
                    </div>
                    <MultiSelectChip
                        v-if="!allLocations"
                        :spaceBetween="false"
                        :title="'Assign this promotion to'"
                        :vertical-chips="true"
                        :vertical-chips-row="true"
                        :extraText="searchingLocations ? 'Searching...' : ''"
                        :options="locationOptions"
                        :selectedValues="selectedLocations"
                        @update="handleSelectLocations"
                    />
                    <div v-else class="text-muted">
                        This promotion will apply to all warehouse locations.
                    </div>
                </Card>
            </div>
            <div class="col-md-6" v-if="!loading && !isCustomerGroupPromotion && promotionType === 'product_discount'">
                <Card :spaceBetween="false" class="my-4 promotion-card" title="Products">
                    <template #headerside>
                        <i @click="showProductModal = true" class="header-icon fa-solid fa-arrow-up-right-from-square"></i>
                    </template>
                    
                    <!-- Product Selection Mode -->
                    <div class="mb-3">
                        <label class="input-label">Apply this promotion to:</label>
                        <div class="product-selection-tabs">
                            <div 
                                v-for="(option, index) in productSelectionModes" 
                                :key="index"
                                :class="['product-selection-tab', { 'active': productSelectionMode === option.value }]"
                                @click="setProductSelectionMode(option.value)"
                            >
                                {{ option.label }}
                            </div>
                        </div>
                    </div>
                    
                    <!-- All Products -->
                    <div v-if="productSelectionMode === 'all'" class="text-muted">
                        This promotion will apply to all products.
                    </div>
                    
                    <!-- Categories -->
                    <MultiSelectChip
                        v-if="productSelectionMode === 'categories'"
                        :title="'Select product categories'"
                        :vertical-chips="true"
                        :vertical-chips-row="true"
                        :extraText="searchingCategories ? 'Searching...' : ''"
                        :options="categoryOptions"
                        :selectedValues="selectedCategories"
                        @search="searchCategories"
                        @update="handleSelectCategories"
                    />
                    
                    <!-- Brands -->
                    <MultiSelectChip
                        v-if="productSelectionMode === 'brands'"
                        :title="'Select product brands'"
                        :vertical-chips="true"
                        :vertical-chips-row="true"
                        :extraText="searchingBrands ? 'Searching...' : ''"
                        :options="brandOptions"
                        :selectedValues="selectedBrands"
                        @search="searchBrands"
                        @update="handleSelectBrands"
                    />
                    
                    <!-- Suppliers -->
                    <MultiSelectChip
                        v-if="productSelectionMode === 'suppliers'"
                        :title="'Select product suppliers'"
                        :vertical-chips="true"
                        :vertical-chips-row="true"
                        :extraText="searchingSuppliers ? 'Searching...' : ''"
                        :options="supplierOptions"
                        :selectedValues="selectedSuppliers"
                        @search="searchSuppliers"
                        @update="handleSelectSuppliers"
                    />

                    <!-- Master Products -->
                    <MultiSelectChip
                        v-if="productSelectionMode === 'master_products'"
                        :title="'Select master products'"
                        :vertical-chips="true"
                        :vertical-chips-row="true"
                        :extraText="masterProductSearchMessage"
                        :options="masterProductOptions"
                        :selectedValues="selectedMasterProducts"
                        @search="searchMasterProducts"
                        @update="selectedMasterProducts = $event"
                    />
                    
                    <!-- Product SKUs -->
                    <MultiSelectChip
                        v-if="productSelectionMode === 'product_skus'"
                        ref="productSkuSelect"
                        :title="'Select products by SKU'"
                        :vertical-chips="true"
                        :vertical-chips-row="true"
                        :extraText="searchingProducts ? 'Searching...' : ''"
                        :options="productSkuOptions"
                        :selectedValues="selectedProductObjects"
                        @search="searchProductsWithSKUs"
                        @update="handleSelectProductsWithSKUs"
                        :storeObjectMode="true"
                        :externalSearch="true"
                    />
                    
                    <!-- SKU Pattern Matching -->
                    <div v-if="productSelectionMode === 'product_skus'" class="sku-pattern-container mt-4">
                        <h5 class="mb-2">SKU Pattern Matching</h5>
                        <div class="pattern-input d-flex mb-3">
                            <CustomInput 
                                type="text"
                                v-model="skuPattern" 
                                placeholder="Enter SKU pattern (e.g., 649004_*)"
                                class="flex-grow-1 mr-2"
                                @input="(value) => { if (value && value.length >= 2) searchProductsByPattern(value) }"
                            />
                            <button 
                                class="btn btn-primary" 
                                @click="addSkuPattern" 
                                :disabled="!skuPattern || skuPattern.length < 2"
                            >
                                Add Pattern
                            </button>
                        </div>
                        
                        <div v-if="searchingByPattern" class="pattern-info mb-2">
                            <div class="text-muted">Searching for matching products...</div>
                        </div>
                        
                        <div v-else-if="patternMatchCount > 0" class="pattern-info mb-3">
                            <div class="d-flex justify-content-between align-items-center">
                                <p class="mb-1 mr-2"><strong>{{ patternMatchCount }}</strong> matching products found</p>
                                <button 
                                    class="btn btn-sm btn-outline-secondary" 
                                    @click="showMatchingProducts = !showMatchingProducts"
                                >
                                    {{ showMatchingProducts ? 'Hide' : 'Show' }} products
                                </button>
                            </div>
                        </div>
                        
                        <div v-if="showMatchingProducts && patternMatchProducts.length > 0" class="matching-products mb-4">
                            <div class="card p-2" style="max-height: 250px; overflow-y: auto;">
                                <ul class="list-unstyled mb-0">
                                    <li v-for="product in patternMatchProducts" :key="product.id" class="py-1 border-bottom">
                                        <div class="d-flex justify-content-between">
                                            <span><strong>{{ product.title }}</strong></span>
                                            <span class="text-muted">{{ product.item_code }}</span>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        
                        <!-- Selected Pattern Chips -->
                        <div v-if="selectedProductObjects.some(item => item.isPattern)" class="sku-patterns mb-3">
                            <h5 class="mb-2">Selected SKU Patterns</h5>
                            <div class="pattern-chips d-flex flex-wrap">
                                <div 
                                    v-for="pattern in selectedProductObjects.filter(item => item.isPattern)"
                                    :key="pattern.id" 
                                    class="pattern-chip bg-light border rounded p-2 mr-2 mb-2 d-flex align-items-center"
                                >
                                    <span>{{ pattern.sku }}</span>
                                    <button 
                                        class="btn btn-sm text-danger ml-2 p-0" 
                                        @click="removeSkuPattern(pattern.sku)"
                                        style="font-size: 16px;"
                                    >×</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
            <div class="col-md-6" v-if="!loading">
                <Card :spaceBetween="false" class="my-4 promotion-card" title="Frequency">
                    <div class="row">
                        <div class="col-12 mb-3">
                            <p class="frequency-instruction">Select one frequency type for this promotion:</p>
                            <div class="frequency-tabs">
                                <div
                                    :class="['frequency-tab', { 'frequency-tab-active': frequency == FREQUENCIES.SPECIFIC_DATE }]"
                                    @click="setFrequencyType(FREQUENCIES.SPECIFIC_DATE)"
                                >
                                    <i class="fas fa-calendar-day mr-2"></i> Specific Dates
                                </div>
                                <div
                                    :class="['frequency-tab', { 'frequency-tab-active': frequency == FREQUENCIES.DATE_RANGE }]"
                                    @click="setFrequencyType(FREQUENCIES.DATE_RANGE)"
                                >
                                    <i class="fas fa-calendar-alt mr-2"></i> Date Range
                                </div>
                                <div
                                    :class="['frequency-tab', { 'frequency-tab-active': frequency == FREQUENCIES.RECURRING }]"
                                    @click="setFrequencyType(FREQUENCIES.RECURRING)"
                                >
                                    <i class="fas fa-sync mr-2"></i> Recurring
                                </div>
                            </div>
                        </div>
                        
                        <div class="col-12">
                            <!-- Specific Dates -->
                            <div v-if="frequency == FREQUENCIES.SPECIFIC_DATE" class="frequency-content">
                                <div
                                    class="frequency-item mb-4"
                                    v-for="specificDate of specificDatesFrequency"
                                    :key="specificDate.id || specificDate.tempId"
                                >
                                    <div class="text-end">
                                        <button @click="removeFrequency(specificDate.id || specificDate.tempId)" class="close-btn">
                                            &times;
                                        </button>
                                    </div>
                                    <p class="input-label mb-1">Select Date</p>
                                    <CustomInput v-model="specificDate.specificDate" type="date" />
                                    <div class="row my-1">
                                        <div class="col-md-6">
                                            <div class="input-label">Start Time</div>
                                            <CustomInput v-model="specificDate.startTime" type="time" />
                                        </div>
                                        <div class="col-md-6">
                                            <div class="input-label">End Time</div>
                                            <CustomInput v-model="specificDate.endTime" type="time" />
                                        </div>
                                    </div>
                                </div>
                                <button class="add-btn" @click="addSpecificDate"><i class="fas fa-plus" /> Add Specific Date</button>
                            </div>
                            
                            <!-- Date Range -->
                            <div v-if="frequency == FREQUENCIES.DATE_RANGE" class="frequency-content">
                                <div class="frequency-item mb-4" v-for="dateRange of dateRangeFrequency" :key="dateRange.id || dateRange.tempId">
                                    <div class="text-end">
                                        <button @click="removeFrequency(dateRange.id || dateRange.tempId)" class="close-btn">
                                            &times;
                                        </button>
                                    </div>
                                    <div class="row my-1">
                                        <div class="col-md-6">
                                            <p class="input-label mb-1">Start Date</p>
                                            <CustomInput v-model="dateRange.fromDate" type="date" />
                                        </div>
                                        <div class="col-md-6">
                                            <p class="input-label mb-1">End Date</p>
                                            <CustomInput v-model="dateRange.toDate" type="date" />
                                        </div>
                                    </div>
                                    <div class="row my-1">
                                        <div class="col-md-6">
                                            <div class="input-label">Start Time</div>
                                            <CustomInput v-model="dateRange.startTime" type="time" />
                                        </div>
                                        <div class="col-md-6">
                                            <div class="input-label">End Time</div>
                                            <CustomInput v-model="dateRange.endTime" type="time" />
                                        </div>
                                    </div>
                                </div>
                                <button class="add-btn" @click="addDateRange"><i class="fas fa-plus" /> Add Date Range</button>
                            </div>
                            
                            <!-- Recurring -->
                            <div v-if="frequency == FREQUENCIES.RECURRING" class="frequency-content">
                                <div
                                    class="frequency-item mb-4"
                                    v-for="recurringDate of recurringDatesFrequency"
                                    :key="recurringDate.id || recurringDate.tempId"
                                >
                                    <div class="text-end">
                                        <button @click="removeFrequency(recurringDate.id || recurringDate.tempId)" class="close-btn">
                                            &times;
                                        </button>
                                    </div>
                                    <div class="mb-2 input-label">Select days of the week this promotion runs:</div>
                                    <div class="weekday-container">
                                        <button
                                            type="button"
                                            :class="[
                                                'weekday-btn', 
                                                { 
                                                    'weekday-btn-active': Array.isArray(recurringDate.daysOfWeek) && 
                                                                          recurringDate.daysOfWeek.includes(weekday.id) 
                                                }
                                            ]"
                                            @click="toggleRecurringDate(recurringDate, weekday.id)"
                                            v-for="(weekday, i) of weekdays"
                                            :key="weekday.id"
                                        >
                                            {{ weekday.title }}
                                        </button>
                                    </div>

                                    <div class="row my-3">
                                        <div class="col-md-6">
                                            <div class="input-label">Start Time</div>
                                            <CustomInput v-model="recurringDate.startTime" type="time" />
                                        </div>
                                        <div class="col-md-6">
                                            <div class="input-label">End Time</div>
                                            <CustomInput v-model="recurringDate.endTime" type="time" />
                                        </div>
                                    </div>
                                    
                                    <div class="my-3">
                                        <div class="custom-checkbox my-2">
                                            <input type="checkbox" :id="'start-date-' + (recurringDate.id || recurringDate.tempId)" v-model="recurringDate.recurrenceStartDateEnabled" />
                                            <label :for="'start-date-' + (recurringDate.id || recurringDate.tempId)">Limit with Start Date</label>
                                        </div>
                                        <CustomInput
                                            :disabled="!recurringDate.recurrenceStartDateEnabled"
                                            v-model="recurringDate.fromDate"
                                            type="date"
                                        />
                                    </div>
                                    
                                    <div class="my-3">
                                        <div class="custom-checkbox my-2">
                                            <input type="checkbox" :id="'end-date-' + (recurringDate.id || recurringDate.tempId)" v-model="recurringDate.recurrenceEndDateEnabled" />
                                            <label :for="'end-date-' + (recurringDate.id || recurringDate.tempId)">Limit with End Date</label>
                                        </div>
                                        <CustomInput
                                            :disabled="!recurringDate.recurrenceEndDateEnabled"
                                            v-model="recurringDate.toDate"
                                            type="date"
                                        />
                                    </div>
                                </div>
                                <button class="add-btn" @click="addRecurrenceDate"><i class="fas fa-plus" /> Add Recurring Schedule</button>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
            <div class="col-md-6" v-if="!loading">
                <Card :spaceBetween="false" class="my-4 promotion-card" :title="isCustomerGroupPromotion ? 'Customer Group Assignment' : 'Customer'">
                    <template #headerside>
                        <i @click="showCustomerModal = true" class="header-icon fa-solid fa-arrow-up-right-from-square"></i>
                    </template>
                    <div v-if="isCustomerGroupPromotion" class="customer-group-info alert alert-info mb-3">
                        <i class="fas fa-info-circle mr-2"></i>
                        This is a Customer Group Promotion. It will apply to all products at all locations for customers in the selected group(s).
                    </div>
                    <MultiSelectChip
                        :title="isCustomerGroupPromotion ? 'Select customer groups for this promotion' : 'Assign this promotion to customer groups'"
                        :vertical-chips="true"
                        :vertical-chips-row="true"
                        :extraText="searchingCustomerGroups ? 'Searching...' : ''"
                        :options="customerGroupOptions"
                        :selectedValues="selectedCustomerGroups"
                        @update="handleSelectCustomerGroups"
                        @search="searchCustomerGroups"
                    />
                    <div v-if="isCustomerGroupPromotion && selectedCustomerGroups.length === 0" class="text-danger mt-2">
                        <small><i class="fas fa-exclamation-circle mr-1"></i> You must select at least one customer group for this promotion type</small>
                    </div>
                </Card>
            </div>
        </div>
    </div>
</template>

<script>
import promotionEditMixin from "./promotion-edit.mixin"

export default promotionEditMixin
</script>
<style lang="scss" scoped>
@import "./promotion-edit.scss";
</style>
